import React, { useMemo, useCallback, Component } from 'react';
import { useViewlogies } from 'context';
import CharacterCloudForm from './CharacterCloudForm';
import WordCloud from './WordCloud';
import { USE_MOCK_DATA, MOCK_CHARACTER_CLOUD_DATA } from './config';
import './characterCloud.scss';

// Simple Error Boundary for WordCloud
class WordCloudErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('[WordCloud] Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // Fallback UI - show empty state instead of crashing
            return (
                <div className="word-cloud-wrapper">
                    <div className="word-cloud-container">
                        <div className="word-cloud-empty">Unable to display word cloud. Please refresh the page.</div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const CharacterCloud = () => {
    const {
        event: { firstName, color },
        characterCloudData,
        fetchCharacterCloud,
        refreshCharacterCloudUntilRendered,
    } = useViewlogies();

    // Use mock data if flag is enabled, otherwise use server-rendered cloud data.
    const cloudData = USE_MOCK_DATA
        ? { words: MOCK_CHARACTER_CLOUD_DATA, imageUrl: null, updatedAt: null }
        : characterCloudData || { words: [], imageUrl: null, updatedAt: null };
    const dataSource = Array.isArray(cloudData) ? cloudData : cloudData.words || [];

    // Create a no-op function when using mock data to prevent server calls
    const handleFetchCharacterCloud = useCallback(() => {
        if (!USE_MOCK_DATA) {
            fetchCharacterCloud();
        }
        // When using mock data, do nothing (no-op)
    }, [fetchCharacterCloud]);

    // After a submission, the server regenerates the SVG asynchronously. Poll briefly so the
    // submitter sees their words appear within a couple of seconds instead of waiting for the
    // next 30s refresh. We capture the currently rendered image's timestamp and stop as soon
    // as a newer one is available.
    const handleSubmitSuccess = useCallback(() => {
        if (USE_MOCK_DATA) {
            return;
        }
        const previousRenderedAt = cloudData.renderedAt || null;
        refreshCharacterCloudUntilRendered(previousRenderedAt);
    }, [cloudData.renderedAt, refreshCharacterCloudUntilRendered]);

    // Don't fetch here - Viewlogy.js handles the initial fetch and interval
    // This prevents duplicate calls

    // Create a stable content hash to detect actual data changes
    const wordsContentHash = useMemo(() => {
        if (!dataSource || dataSource.length === 0) {
            return 'empty';
        }
        try {
            return dataSource
                .map(w => `${w.text || w.word || w.adjective}:${w.value || w.count || w.frequency}`)
                .sort()
                .join('|');
        } catch (error) {
            console.warn('[CharacterCloud] Error generating content hash:', error);
            return 'error';
        }
    }, [dataSource]);

    // Memoize words to prevent unnecessary re-renders when content is the same
    const wordsToDisplay = useMemo(() => {
        return dataSource || [];
    }, [wordsContentHash, dataSource]);

    // Use wordsContentHash directly as the key - it already represents the content
    const wordCloudKey = wordsContentHash;

    return (
        <div className="character-cloud-section">
            <CharacterCloudForm
                firstName={firstName}
                color={color}
                onSuccess={handleSubmitSuccess}
                fetchCharacterCloud={handleFetchCharacterCloud}
            />
            <WordCloudErrorBoundary>
                <div className="word-cloud-wrapper">
                    <WordCloud
                        key={wordCloudKey}
                        words={wordsToDisplay}
                        firstName={firstName}
                        imageUrl={cloudData.imageUrl}
                        updatedAt={cloudData.updatedAt}
                        renderedAt={cloudData.renderedAt}
                    />
                </div>
            </WordCloudErrorBoundary>
        </div>
    );
};

export default CharacterCloud;
